import type { Building } from "building-types/building/buildingTypes";
import { ControllerType } from "building-types/electrical/electricalTypesEnum";
import type { LoadView } from "building-types/electrical/electricalViewTypes";
import { LinkViewType } from "building-types/electrical/electricalViewTypesEnum";
import type { SheetGeometryCategoryTable } from "building-types/views/sheetGeometryCategory";
import { SheetGeometryCategory } from "building-types/views/sheetGeometryCategory";
import type { SheetGeometry } from "building-types/views/sheetGeometryTypes";
import {
  SheetGeometryType,
  sheetPolylineGeometryNew,
  sheetSVGGeometryNew,
  sheetTextGeometryNew,
} from "building-types/views/sheetGeometryTypes";
import colors from "common/constants/colorsOld";
import { CollisionType, planColliderObstacleNew } from "doc/plans/interiorLabelObstacle";
import type { Obstacles } from "doc/plans/planGeometrySource";
import type { RelaxationObstacle } from "doc/plans/planGeometrySourceTypes";
import { aabb2ContainsVec2 } from "geom/aabb2";
import { polyLine2FromAABB2 } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { generateContLoadConnection } from "lib/house/annotation/electrical/ContLoadConnection";
import { generateController } from "lib/house/annotation/electrical/Controller";
import { generateLoad, hasPlanLabel, loadPlanLabelSheetPlacement } from "lib/house/annotation/electrical/Load";
import { generateLoadLoadConnection } from "lib/house/annotation/electrical/LoadLoadConnection";
import { docEntityHandleInReportingUnit, reportingUnitRoomRegionBBs } from "lib/house/plans/reportingUnitRooms";
import { planSVGBezierStringToPolyLine2 } from "svg/svgBezierStringToPolyLine2";
import { SVGBoundingBoxProvider } from "svg/svgBoundingBoxProvider";

interface PlanElectricalLinksProps {
  building: Building;
  levelId: string;
  selectedReportingUnitLabelId?: string;
}

export async function makeSheetGeometryFromElectricalLinks(props: PlanElectricalLinksProps): Promise<SheetGeometry[]> {
  const { building, levelId, selectedReportingUnitLabelId } = props;
  const { electricalSystemView, levels } = building;
  const electricalViewTable = electricalSystemView[levelId];
  const handle = levels[levelId].handle;

  const links = electricalViewTable.links ?? [];
  const regionBBs = reportingUnitRoomRegionBBs(building, selectedReportingUnitLabelId);

  const result: SheetGeometry[] = [];

  for (const {
    start: { positionX: sx, positionY: sy, normalX: snx, normalY: sny },
    end: { positionX: ex, positionY: ey },
    type,
    flipGuideVectorRotation,
  } of links) {
    if (
      regionBBs &&
      !regionBBs.some((region) => aabb2ContainsVec2(region, sx, sy) || aabb2ContainsVec2(region, ex, ey))
    ) {
      continue;
    }
    const beziers: PolyLine2[] = [];
    switch (type) {
      case LinkViewType.ControllerLoad: {
        const connection = generateContLoadConnection({
          scale: 1,
          controllerPosition: [sx, sy],
          controllerNormal: [snx, -sny],
          loadPosition: [ex, ey],
          normalAdjust: 0.5,
          flipGuideVectorRotation,
        });
        const pl = await planSVGBezierStringToPolyLine2(connection);
        beziers.push(...pl);
        break;
      }
      case LinkViewType.LoadLoad: {
        const connection = generateLoadLoadConnection({
          scale: 1,
          load1Position: [sx, sy],
          load2Position: [ex, ey],
          flipGuideVectorRotation,
        });
        const pl = await planSVGBezierStringToPolyLine2(connection);
        beziers.push(...pl);
        break;
      }
      default: {
        break;
      }
    }
    result.push(
      sheetPolylineGeometryNew({
        handle: handle,
        paths: beziers,
      }),
    );
  }
  return result;
}

interface PlanElectricalLoadsProps {
  building: Building;
  levelId: string;
  planRotationRads?: number;
  filter?: (loadView: LoadView) => boolean;
  svgTextScale?: number;
  scale?: number;
  color?: string;
  selectedReportingUnitLabelId?: string;
  /** Relaxed categories for this render; ELECTRIC_LOAD_LABEL among them is what moves round
   * ceiling-light labels onto the direction-aware offset. See planSetComputeServiceBase.ts. */
  relaxationInteractionMap?: SheetGeometryCategoryTable<RelaxationObstacle[]>;
}

export function makeSheetGeometryFromElectricalLoads(props: PlanElectricalLoadsProps): SheetGeometry[] {
  const result: SheetGeometry[] = [];
  const {
    building,
    levelId,
    planRotationRads = 0,
    filter,
    svgTextScale = 1,
    scale = 1,
    color = colors.black,
    selectedReportingUnitLabelId,
  } = props;
  const { electricalSystemView } = building;
  const electricalViewTable = electricalSystemView[levelId];

  if (electricalViewTable) {
    const { loads } = electricalViewTable;

    for (const load of loads) {
      if (filter && !filter(load)) {
        continue;
      }
      if (!docEntityHandleInReportingUnit(building, selectedReportingUnitLabelId, load.handle)) {
        continue;
      }
      const { positionX, positionY, normalX, normalY, levelIdIndicator, fixControllerOverlap, loadDescriptor, handle } =
        load;
      const loadPath = generateLoad({
        // The plan label is rendered separately as its own ELECTRIC_LOAD_LABEL geometry (see
        // makeSheetGeometryFromElectricalLoadLabels) so it can be relaxed independently of the
        // symbol; omit it here so it isn't drawn twice.
        loadDescriptor: { ...loadDescriptor, planLabel: undefined },
        normal: [normalX, normalY],
        levelId: levelIdIndicator,
        floorIndex: building.levels[levelIdIndicator ?? ""]?.floorIndex,
        posFromController: fixControllerOverlap,
        planRotationRads,
        svgTextScale,
        scale,
        strokeDarkPrimary: color,
        textFillPrimary: color,
        fillDarkPrimary: color,
        switchedOutletFillColor: color,
      });
      result.push(
        sheetSVGGeometryNew({
          handle: handle,
          inPlanScale: false,
          x: positionX,
          y: positionY,
          path: loadPath,
          buildingEntity: load,
        }),
      );
    }
  }
  return result;
}

export function makeSheetGeometryFromElectricalLoadLabels(props: PlanElectricalLoadsProps): SheetGeometry[] {
  const result: SheetGeometry[] = [];
  const {
    building,
    levelId,
    planRotationRads = 0,
    filter,
    scale = 1,
    selectedReportingUnitLabelId,
    relaxationInteractionMap,
  } = props;
  const reduceOverlapLoadLabels =
    relaxationInteractionMap !== undefined && SheetGeometryCategory.ELECTRIC_LOAD_LABEL in relaxationInteractionMap;
  const { electricalSystemView } = building;
  const electricalViewTable = electricalSystemView[levelId];

  if (electricalViewTable) {
    const { loads } = electricalViewTable;

    for (const load of loads) {
      if (filter && !filter(load)) {
        continue;
      }
      if (!docEntityHandleInReportingUnit(building, selectedReportingUnitLabelId, load.handle)) {
        continue;
      }
      const { positionX, positionY, normalX, normalY, loadDescriptor, handle } = load;
      // A blank label would otherwise reach the relaxer as an anchored but invisible text box,
      // which still earns a leader line.
      if (!hasPlanLabel(loadDescriptor.planLabel)) {
        continue;
      }

      const placement = loadPlanLabelSheetPlacement({
        loadDescriptor,
        normal: [normalX, normalY],
        scale,
        planRotationRads,
        reduceOverlapLoadLabels,
      });
      if (!placement) {
        continue;
      }

      // anchorPoint ties the label back to the symbol's position so the relaxer can draw a leader
      // once it's moved far enough away - see the anchorPoint-only branch in sheetGeometryGradientDescent.ts.
      // x/y stay on the symbol; the offset is a sheet-inch style field because this generator has no
      // view scale, so an offset baked into x/y is only ever right at one of them.
      const [offsetX, offsetY] = placement.sheetOffset;
      // No text style is set here on purpose. resolveSheetGeometryStyle merges the geometry LAST, so
      // anything set inline outranks the category default, the view template's styleOverrides and a
      // STYLING effect's textStyleOverrides alike - baking fontSize/fontColor makes the Text Size and
      // Text Color controls inert. The default size lives in planStyleSheets under this category.
      result.push(
        sheetTextGeometryNew({
          handle,
          text: placement.text,
          x: positionX,
          y: positionY,
          offsetX,
          offsetY,
          rotation: placement.uprightAngleRads,
          placementXAnchor: placement.placementXAnchor,
          placementYAnchor: placement.placementYAnchor,
          anchorPoint: { x: positionX, y: positionY },
          // Same entity the symbol carries, so per-entity built-in filters (see
          // planGeometryBuiltInPlanFilters) skip a label alongside the symbol it belongs to.
          buildingEntity: load,
        }),
      );
    }
  }
  return result;
}

interface PlanElectricalControllerProps {
  building: Building;
  levelId: string;
  planRotationRads: number;
  scale?: number;
  color?: string;
  selectedReportingUnitLabelId?: string;
}

export function makeSheetGeometryFromElectricalControllers(props: PlanElectricalControllerProps): SheetGeometry[] {
  const { building, levelId, planRotationRads, scale = 1, color = colors.black, selectedReportingUnitLabelId } = props;
  const { electricalSystemView } = building;
  const electricalViewTable = electricalSystemView[levelId];

  const result: SheetGeometry[] = [];

  if (electricalViewTable) {
    const { controllers } = electricalViewTable;

    for (const controller of controllers) {
      const { positionX, positionY, normalX, normalY, handle, planTag } = controller;
      if (!docEntityHandleInReportingUnit(building, selectedReportingUnitLabelId, handle)) {
        continue;
      }
      const switchType = controller.type === ControllerType.SWITCH ? controller.switchType : undefined;
      const controllerSymbol = generateController({
        switchType,
        normal: [normalX, normalY],
        planRotationRads,
        scale,
        strokeDarkPrimary: color,
        textFillPrimary: color,
        dimmerMarkerAsText: true,
        planTag,
      });
      result.push(
        sheetSVGGeometryNew({
          handle: handle,
          inPlanScale: false,
          x: positionX,
          y: positionY,
          path: controllerSymbol,
          buildingEntity: controller,
        }),
      );
    }
  }
  return result;
}

export function collectElectricalObstacles(obstacles: Obstacles, geom: SheetGeometry) {
  if (geom.type === SheetGeometryType.SVG) {
    const aabb = SVGBoundingBoxProvider.instance.getAABB2FromSVGString(geom.path, geom.x, geom.y, 0, 0.5, -0.5);
    if (aabb) {
      obstacles.obstacles?.push(planColliderObstacleNew(CollisionType.ElectricLoad, polyLine2FromAABB2(aabb)));
    }
  }
}
